/**
 * CANONICAL MODULE — app-platform/shared/themeMode.ts
 *
 * Copied byte-for-byte into every app that takes the theme engine (`src/lib/themeMode.ts`). Pure: no
 * DOM, no imports; storage is injected, so it tests without a browser. **Fix bugs HERE, then sync
 * outward.**
 *
 * ── What it is ───────────────────────────────────────────────────────────────────────────────────
 * The mode vocabulary and the boot mirror (design/W-theme-contract.md §5):
 *
 *   - Stored preference `ThemePref`: `system | light | dark | oled | custom:<id>`. Resolved mode
 *     `ThemeMode`: `light | dark | oled`. `system` is a choice, not a palette: it resolves to light or
 *     dark from `prefers-color-scheme` (or to `oled` when the app's "true black" option says so).
 *     A custom theme is pinned: `system` never switches into or out of it.
 *   - The mirror, `${storageKey}.theme.boot` in localStorage: the one thing the contract requires
 *     about storage. The app writes it on every apply; `boot-theme.js` reads it before first paint.
 *     `readMirror` never throws and returns null for anything malformed; `writeMirror` returns false
 *     instead of throwing when storage is blocked or full.
 *   - `resolveBoot(mirror, systemDark)`: exactly what `boot-theme.js` does with the mirror, as a
 *     function. themeBoot.test.ts runs the script against a fake document and checks the two agree on
 *     every case, so the script cannot drift from this file.
 *
 * ── As built beyond §5 (2026-11-01, task 3.3b) ───────────────────────────────────────────────────
 * `resolved.vars` is not custom-only: any inline `--theme-*` the app applied (quil's solved ground,
 * contently's solved accent) rides in it, so it paints on frame one. And `alt` holds the vars and bg
 * for the other scheme(s), because a `system` preference can flip before the app loads: vars are
 * only applied when they were solved for the mode the boot resolves now (`resolved.theme`, else
 * `alt[mode]`), never across schemes.
 */

export type ThemeMode = 'light' | 'dark' | 'oled'
export type ThemePref = 'system' | ThemeMode | `custom:${string}`

export const THEME_MODES: readonly ThemeMode[] = ['light', 'dark', 'oled']

/** Inline vars and the page colour for one mode. Keys are `--theme-*` (others are dropped on read). */
export interface ModePaint {
  vars?: Record<string, string>
  /** `--theme-bg` for that mode, for `meta[name=theme-color]`. */
  bg?: string
}

export interface BootMirror {
  v: 1
  pref: ThemePref
  /** What `system` resolves to on a dark system: `dark`, or `oled` with the app's true-black option. */
  systemDark?: 'dark' | 'oled'
  /** What the app last applied. */
  resolved?: ModePaint & { theme: ThemeMode; customId?: string }
  /** Paint for the other modes, so a system flip before the app loads still paints right. */
  alt?: Partial<Record<ThemeMode, ModePaint>>
}

/** The storage the mirror needs; `window.localStorage` in an app, a Map-backed fake in tests. */
export interface MirrorStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

/** Exported for tests only (app-platform 8.3): no app code calls it. */
export const mirrorKey = (storageKey: string): string => `${storageKey}.theme.boot`
/** Where an app keeps its device-only custom themes (§5). The shape is the app's (3.6). */
export const customThemesKey = (storageKey: string): string => `${storageKey}.theme.custom`

/** A value the boot script will write inline: a hex or an rgb()/hsl() literal, nothing that could
 *  smuggle `;` or `url(` into a style declaration. */
const SAFE_VALUE = /^(#[0-9a-fA-F]{3,8}|(rgb|rgba|hsl|hsla)\([0-9.,%\s/+-]*\))$/
const VAR_NAME = /^--theme-[a-z0-9-]+$/

export function isThemeMode(x: unknown): x is ThemeMode {
  return x === 'light' || x === 'dark' || x === 'oled'
}

/**
 * A stored preference, or null. `custom:` needs a non-empty id of [A-Za-z0-9_-], at most 64.
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export function parsePref(x: unknown): ThemePref | null {
  if (x === 'system' || isThemeMode(x)) return x
  if (typeof x === 'string' && /^custom:[A-Za-z0-9_-]{1,64}$/.test(x)) return x as ThemePref
  return null
}

const customIdOf = (pref: ThemePref): string | null => (pref.startsWith('custom:') ? pref.slice(7) : null)

/**
 * Resolve a preference to a mode. `system` follows the OS (`systemDark`), using `darkAs` for dark
 * (`'oled'` for a true-black option). A custom preference resolves to that theme's own scheme when
 * the caller knows it (`customScheme`), and to what `system` would give when it does not (the theme
 * was removed): never a throw.
 */
export function resolveMode(
  pref: ThemePref,
  env: { systemDark: boolean; darkAs?: 'dark' | 'oled'; customScheme?: (id: string) => ThemeMode | undefined },
): { theme: ThemeMode; customId?: string } {
  const id = customIdOf(pref)
  if (id !== null) {
    const scheme = env.customScheme?.(id)
    if (scheme) return { theme: scheme, customId: id }
    return resolveMode('system', env)
  }
  if (isThemeMode(pref)) return { theme: pref }
  return { theme: env.systemDark ? (env.darkAs ?? 'dark') : 'light' }
}

/** `light` or `dark`, for `data-scheme` and `color-scheme` (`oled` is a dark). */
export const schemeOf = (mode: ThemeMode): 'light' | 'dark' => (mode === 'light' ? 'light' : 'dark')

function cleanPaint(x: unknown): ModePaint | null {
  if (typeof x !== 'object' || x === null) return null
  const o = x as Record<string, unknown>
  const out: ModePaint = {}
  if (typeof o.bg === 'string' && SAFE_VALUE.test(o.bg.trim())) out.bg = o.bg.trim()
  if (typeof o.vars === 'object' && o.vars !== null) {
    const vars: Record<string, string> = {}
    for (const [k, v] of Object.entries(o.vars as Record<string, unknown>)) {
      if (VAR_NAME.test(k) && typeof v === 'string' && SAFE_VALUE.test(v.trim())) vars[k] = v.trim()
    }
    out.vars = vars
  }
  return out
}

/**
 * Parse a mirror from its stored text. Unknown or unsafe parts are dropped (a var that is not
 * `--theme-*` or not a plain colour literal never reaches the page); a mirror without a valid `pref`
 * is null.
 * Exported for tests only (app-platform 8.3): no app code calls it.
 */
export function parseMirror(text: string | null): BootMirror | null {
  if (text === null) return null
  let o: unknown
  try {
    o = JSON.parse(text)
  } catch {
    return null
  }
  if (typeof o !== 'object' || o === null) return null
  const r = o as Record<string, unknown>
  if (r.v !== 1) return null
  const pref = parsePref(r.pref)
  if (pref === null) return null
  const m: BootMirror = { v: 1, pref }
  if (r.systemDark === 'dark' || r.systemDark === 'oled') m.systemDark = r.systemDark
  const res = r.resolved as Record<string, unknown> | undefined
  if (res && typeof res === 'object' && isThemeMode(res.theme)) {
    const paint = cleanPaint(res) ?? {}
    m.resolved = { theme: res.theme, ...paint }
    if (typeof res.customId === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(res.customId)) m.resolved.customId = res.customId
  }
  if (typeof r.alt === 'object' && r.alt !== null) {
    const alt: Partial<Record<ThemeMode, ModePaint>> = {}
    for (const mode of THEME_MODES) {
      const p = cleanPaint((r.alt as Record<string, unknown>)[mode])
      if (p) alt[mode] = p
    }
    m.alt = alt
  }
  return m
}

export function readMirror(storage: MirrorStorage | null | undefined, storageKey: string): BootMirror | null {
  try {
    return parseMirror(storage?.getItem(mirrorKey(storageKey)) ?? null)
  } catch {
    return null
  }
}

export function writeMirror(storage: MirrorStorage | null | undefined, storageKey: string, mirror: BootMirror): boolean {
  if (!storage) return false
  try {
    storage.setItem(mirrorKey(storageKey), JSON.stringify(mirror))
    return true
  } catch {
    return false
  }
}

/** What the boot paints: the mode, the custom id when one is active, the inline vars and the page
 *  colour (each only when they were solved for this mode). */
interface BootPaint {
  theme: ThemeMode
  scheme: 'light' | 'dark'
  customId?: string
  vars: Record<string, string>
  bg?: string
  /** True when the paint follows the OS (the boot keeps listening for a system flip). */
  followsSystem: boolean
}

/**
 * The boot decision, as a function (boot-theme.js is this, in ES5). No mirror, or a malformed one,
 * is `system`. A custom preference paints its theme only when the mirror's `resolved` is that very
 * custom theme; otherwise it falls back to `system`, never to a stale palette.
 *
 * `defaultBg` is the script tag's `data-bg-<mode>`: the page colour for `meta[name=theme-color]`
 * when the paint carries none (oled falls back to dark). Without it, `bg` is absent and the script
 * leaves index.html's own colour. `pin` is the mode the script tag's `data-pin` gives this page's
 * path (2026-11-01); the script resolves the path and its `data-pin-unless` flag before calling this.
 *
 * @internal The executable specification of `boot-theme.js` (app-platform 8.3): themeBoot.test.ts
 * holds the shipped script to it on every case. No app code calls it, so no bundle carries it.
 */
export function resolveBoot(
  mirror: BootMirror | null,
  systemDark: boolean,
  defaultBg: Partial<Record<ThemeMode, string>> = {},
  pin?: ThemeMode,
): BootPaint {
  const p = pin === undefined ? resolvePaint(mirror, systemDark) : builtInPaint(mirror, pin, false)
  const bg = p.bg ?? defaultBg[p.theme] ?? (p.theme === 'oled' ? defaultBg.dark : undefined)
  return bg === undefined ? p : { ...p, bg }
}

/** A built-in mode's paint: the vars and bg the mirror solved for exactly that mode, else none. A
 *  pinned path (`data-pin`) is this for its mode whatever the OS, the preference or a pasted theme,
 *  and never follows a flip. */
function builtInPaint(mirror: BootMirror | null, theme: ThemeMode, followsSystem: boolean): BootPaint {
  const res = mirror?.resolved
  const paint: ModePaint | undefined = res && !res.customId && res.theme === theme ? res : mirror?.alt?.[theme]
  return { theme, scheme: schemeOf(theme), vars: { ...(paint?.vars ?? {}) }, ...(paint?.bg ? { bg: paint.bg } : {}), followsSystem }
}

function resolvePaint(mirror: BootMirror | null, systemDark: boolean): BootPaint {
  const pref: ThemePref = mirror?.pref ?? 'system'
  const res = mirror?.resolved
  const id = customIdOf(pref)
  if (id !== null && res && res.customId === id) {
    return { theme: res.theme, scheme: schemeOf(res.theme), customId: id, vars: { ...(res.vars ?? {}) }, ...(res.bg ? { bg: res.bg } : {}), followsSystem: false }
  }
  const effective: ThemePref = id !== null ? 'system' : pref
  const theme = resolveMode(effective, { systemDark, darkAs: mirror?.systemDark ?? 'dark' }).theme
  return builtInPaint(mirror, theme, effective === 'system')
}
